<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import ProductCard from '@/Components/Public/ProductCard.vue'

// Two-row stepping category carousel for the public home. The category's items
// split across two rows that auto-advance ONE card every HOLD_MS, holding
// between steps so each card is easy to focus on. The rows ping-pong (reverse
// at the ends — no jarring wrap) and start in opposite directions (top → right,
// bottom → left). Stylish prev/next arrows step both rows manually on desktop;
// pause on hover. Mobile / prefers-reduced-motion: native swipe, no auto-step,
// no arrows. Cards are the shared ProductCard. ProductRail is unaffected.
const props = defineProps({
  title:       { type: String, required: true },
  kind:        { type: String, required: true }, // vehicle | tour | property
  items:       { type: Array,  default: () => [] },
  viewAllHref: { type: String, default: '' },
  itemBase:    { type: String, default: '' },     // card link = `${itemBase}/${id}`
})

const { t } = useI18n()

const HOLD_MS = 4000
// Fill a sparse row up to this many cards so it spans a wide viewport (no empty
// gap to step into). Typical home rows already exceed it, so no duplication.
const MIN_PER_ROW = 8

const accentVar = computed(() => (
  { vehicle: 'var(--accent)', tour: 'var(--warn)', property: 'var(--second)' }[props.kind] || 'var(--accent)'
))

// Split featured items into two rows; fill each up to MIN_PER_ROW.
const half = computed(() => Math.ceil(props.items.length / 2))
function fill(slice) {
  if (!slice.length) return []
  const reps = Math.ceil(MIN_PER_ROW / slice.length)
  return Array.from({ length: reps }, () => slice).flat()
}
const rowA = computed(() => fill(props.items.slice(0, half.value)))
const rowB = computed(() => fill(props.items.slice(half.value)))

const trackA = ref(null)
const trackB = ref(null)
const dirA = { v: 1 }   // top row starts moving right…
const dirB = { v: -1 }  // …bottom row starts moving left (opposite)
let timer = null
let stepping = false
let mql = null

// One card's stride = distance between the first two items (card width + gap).
function unitOf(el) {
  const items = el?.querySelectorAll('.mq__item')
  if (!items || items.length < 2) return 0
  return items[1].offsetLeft - items[0].offsetLeft
}

// Auto-advance a row one card, reversing direction at either end (ping-pong).
function autoStep(el, dir) {
  if (!el) return
  const unit = unitOf(el)
  if (!unit) return
  const max = el.scrollWidth - el.clientWidth
  if (max <= 1) return // everything already fits — nothing to step
  if (el.scrollLeft >= max - 1) dir.v = -1
  else if (el.scrollLeft <= 1) dir.v = 1
  el.scrollTo({ left: el.scrollLeft + dir.v * unit, behavior: 'smooth' })
}

function tick() {
  autoStep(trackA.value, dirA)
  autoStep(trackB.value, dirB)
}

// Arrow: nudge both rows one card the same way, then restart the auto timer so
// it doesn't immediately fire on top of the manual step.
function nudge(delta) {
  [trackA.value, trackB.value].forEach((el) => {
    if (!el) return
    const unit = unitOf(el)
    if (unit) el.scrollBy({ left: delta * unit, behavior: 'smooth' })
  })
  restart()
}
const next = () => nudge(1)
const prev = () => nudge(-1)

function startTimer() { if (stepping && !timer) timer = setInterval(tick, HOLD_MS) }
function stopTimer() { if (timer) { clearInterval(timer); timer = null } }
function restart() { stopTimer(); startTimer() }

function syncMode() {
  stepping = mql.matches
  stopTimer()
  if (stepping) {
    // Start the bottom row at its far end so it has room to travel left
    // (keeps the two rows visually opposite).
    nextTick(() => {
      const b = trackB.value
      if (b) b.scrollLeft = b.scrollWidth - b.clientWidth
      startTimer()
    })
  }
}

onMounted(() => {
  mql = window.matchMedia('(min-width: 768px) and (prefers-reduced-motion: no-preference)')
  mql.addEventListener('change', syncMode)
  syncMode()
})
onBeforeUnmount(() => {
  stopTimer()
  mql?.removeEventListener('change', syncMode)
})
</script>

<template>
  <section v-if="items.length" class="mqs" :style="{ '--mq-accent': accentVar, '--card-accent': accentVar }">
    <div class="mqs__head">
      <h2 class="mqs__title">
        <span class="mqs__icon">
          <svg v-if="kind === 'vehicle'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="5.5" cy="17.5" r="3.5" /><circle cx="18.5" cy="17.5" r="3.5" />
            <path d="M15 5.5a1 1 0 1 0 0-.01" /><path d="M12 17.5V14l-3-3 4-3 2.5 3.5H18" />
          </svg>
          <svg v-else-if="kind === 'tour'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" /><polygon points="16.2 7.8 13.4 13.4 7.8 16.2 10.6 10.6" />
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M3 10.5 12 3l9 7.5" /><path d="M5 10v9.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V10" /><path d="M9.5 20.5V15h5v5.5" />
          </svg>
        </span>
        {{ title }}
      </h2>
      <a v-if="viewAllHref" :href="viewAllHref" class="mqs__all">{{ t('public.home.view_all') }} →</a>
    </div>

    <div class="mqs__rows" @mouseenter="stopTimer" @mouseleave="startTimer">
      <button type="button" class="mq-arrow mq-arrow--prev" :aria-label="t('public.home.prev')" @click="prev">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
      </button>

      <div ref="trackA" class="mq">
        <ul class="mq__track">
          <li v-for="(it, i) in rowA" :key="`a-${i}`" class="mq__item">
            <ProductCard :kind="kind" :item="it" :href="`${itemBase}/${it.id}`" />
          </li>
        </ul>
      </div>

      <div v-if="rowB.length" ref="trackB" class="mq">
        <ul class="mq__track">
          <li v-for="(it, i) in rowB" :key="`b-${i}`" class="mq__item">
            <ProductCard :kind="kind" :item="it" :href="`${itemBase}/${it.id}`" />
          </li>
        </ul>
      </div>

      <button type="button" class="mq-arrow mq-arrow--next" :aria-label="t('public.home.next')" @click="next">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
      </button>
    </div>
  </section>
</template>

<style scoped>
.mqs { margin-top: 2.75rem; }
.mqs:first-child { margin-top: 0; }
.mqs__head {
  display: flex; align-items: center; justify-content: space-between; gap: 1rem;
  margin-bottom: 1.1rem;
}
.mqs__title {
  display: inline-flex; align-items: center; gap: 0.6rem;
  font-family: var(--font-display, var(--display));
  font-weight: 800; font-size: clamp(1.4rem, 3vw, 1.9rem);
  letter-spacing: -0.02em; color: var(--ink); margin: 0;
}
.mqs__icon {
  display: grid; place-items: center;
  width: 2.4rem; height: 2.4rem; border-radius: 0.7rem;
  background: color-mix(in oklch, var(--mq-accent) 14%, var(--surface));
  color: var(--mq-accent); flex: none;
}
.mqs__icon svg { width: 1.4rem; height: 1.4rem; }
.mqs__all {
  font-weight: 700; font-size: 1rem; color: var(--mq-accent);
  text-decoration: none; white-space: nowrap;
}
.mqs__all:hover { text-decoration: underline; }
.mqs__all:focus-visible { outline: 2px solid var(--mq-accent); outline-offset: 2px; border-radius: 2px; }

/* ---- Rows + arrows ------------------------------------------------------ */
.mqs__rows { position: relative; }

.mq {
  overflow-x: auto; scroll-snap-type: x mandatory;
  scrollbar-width: none; margin-top: 1rem;
}
.mq:first-of-type { margin-top: 0; }
.mq::-webkit-scrollbar { display: none; }
.mq__track {
  display: flex; gap: 1rem; width: max-content;
  list-style: none; margin: 0; padding: 0.25rem;
}
.mq__item { flex: 0 0 auto; width: min(80vw, 17rem); scroll-snap-align: start; }

/* Stylish round step arrows — brand tokens, desktop only. */
.mq-arrow {
  position: absolute; top: 50%; transform: translateY(-50%); z-index: 3;
  width: 3rem; height: 3rem; border-radius: 999px;
  display: grid; place-items: center;
  background: var(--surface); border: 1px solid var(--line); color: var(--ink);
  cursor: pointer;
  box-shadow: 0 10px 26px -12px color-mix(in oklch, var(--ink) 45%, transparent);
  transition: transform 150ms ease, border-color 150ms ease, color 150ms ease, background 150ms ease;
}
.mq-arrow svg { width: 1.4rem; height: 1.4rem; }
.mq-arrow:hover {
  color: var(--mq-accent); border-color: var(--mq-accent);
  background: color-mix(in oklch, var(--mq-accent) 8%, var(--surface));
  transform: translateY(-50%) scale(1.06);
}
.mq-arrow:focus-visible { outline: 2px solid var(--mq-accent); outline-offset: 2px; }
.mq-arrow--prev { left: -0.75rem; }
.mq-arrow--next { right: -0.75rem; }

/* Touch + reduced motion: native swipe, no arrows. */
@media (max-width: 767px), (prefers-reduced-motion: reduce) {
  .mq-arrow { display: none; }
}
</style>
